import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Typography } from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface BioType {
  children: Array<{ text: string }>;
}

interface SideBarProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  url: string;
  image?: string;
  biography?: BioType[];
}

const SideBar: React.FC<SideBarProps> = ({ isOpen, onClose, name, url, image, biography }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
          />

          {/* Sidebar - Full screen on mobile, 35vw on desktop */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 h-full w-full max-w-full md:w-[35vw] bg-gray-100 shadow-2xl z-50 flex flex-col p-4 md:p-6 overflow-y-auto"
          >
            {/* Close Button - Larger on mobile for better touch */}
            <div className="flex justify-end mb-4 md:mb-0">
              <button
                onClick={onClose}
                className="hover:bg-red-50 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-colors duration-200"
              >
                <XMarkIcon className="h-6 w-6 md:h-7 md:w-7 text-red-500" />
              </button>
            </div>

            {/* Person Image - Adjusted size for mobile */}
            <div className="flex justify-center mb-4 md:mb-4">
              {image && (
                <img
                  src={image}
                  alt={name}
                  className="w-24 h-24 md:w-36 md:h-36 object-cover rounded-lg border-4 border-gray-100 shadow-md mx-auto"
                />
              )}
            </div>

            {/* Name - Responsive text size */}
            <Typography variant="h3" color="blue-gray" className="mb-2 text-center font-bold text-xl md:text-3xl px-2">
              {name}
            </Typography>

            {/* URL - Responsive text size */}
            {url && (
              <Typography 
                variant="paragraph" 
                color="gray" 
                className="mb-4 md:mb-6 text-center text-xs md:text-sm font-semibold px-2"
              >
                {url}
              </Typography>
            )}

            {/* Separator Line */}
            <hr className="my-4 border-gray-300 mx-2" />

            {/* Biography Title - Responsive text size */}
            <Typography variant="h4" color="red" className="mb-3 md:mb-4 font-semibold text-lg md:text-xl px-2">
              Biography
            </Typography>

            {/* Biography Content - Responsive text size */}
            {biography && biography.length > 0 ? (
              <div className="space-y-3 md:space-y-4 px-2">
                {biography.map((b, i) => (
                  <Typography 
                    key={i} 
                    variant="paragraph" 
                    color="blue-gray" 
                    className="leading-relaxed text-sm md:text-base break-words"
                  >
                    {b.children[0].text}
                  </Typography>
                ))}
              </div>
            ) : (
              <Typography variant="paragraph" color="gray" className="italic mt-4 text-sm md:text-base px-2">
                There is no Bio for this person.
              </Typography>
            )}

            {/* Close Button at Bottom - Larger on mobile */}
            <div className="mt-auto pt-6 px-2">
              <Button
                color="red"
                variant="filled"
                className="w-full md:w-auto rounded-full py-3 md:py-3 text-sm md:text-base px-6"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SideBar;